import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, X, ExternalLink } from 'lucide-react';

const navLinks = [
  { href: '#overview', label: 'Overview' },
  { href: '#distribution', label: 'Distribution' },
  { href: '#vesting', label: 'Vesting' },
  { href: '#tge', label: 'TGE Supply' },
  { href: '#transparency', label: 'Transparency' },
  { href: '#data', label: 'Data & API' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navLinks.map(link => link.href.slice(1));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToSection = (href) => {
    const element = document.getElementById(href.slice(1));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileOpen(false);
  };
  
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${isScrolled 
          ? 'glass-dark py-3' 
          : 'bg-transparent py-5'
        }
      `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_neuromesh-token/artifacts/xe1d400s_NeuroMesh%20Logo%20%282%29.png" 
                alt="NeuroMesh Logo" 
                className="w-10 h-10 rounded-lg object-contain"
              />
              <div className="absolute inset-0 rounded-lg bg-primary/20 blur-lg group-hover:blur-xl transition-all opacity-50" />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-semibold text-foreground text-lg">
                NeuroMesh
              </span>
              <span className="text-xs text-primary ml-2 font-medium">
                TOKENOMICS
              </span>
            </div>
          </a>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className={`
                  px-3 py-2 rounded-lg text-sm font-medium transition-all
                  ${activeSection === link.href.slice(1)
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }
                `}
              >
                {link.label}
              </button>
            ))}
          </nav>
          
          {/* CTA Buttons */}
          <div className="flex items-center gap-3">
            <a 
              href="https://neuro-mesh.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:block"
            >
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                Main Site
                <ExternalLink className="w-3 h-3 ml-1.5" />
              </Button>
            </a>
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 hidden sm:flex"
              onClick={() => scrollToSection('#data')}
            >
              Download Data
            </Button>
            
            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="glass-dark border-border w-[280px]">
                <nav className="flex flex-col gap-2 mt-8">
                  {navLinks.map((link) => (
                    <button
                      key={link.href}
                      onClick={() => scrollToSection(link.href)}
                      className={`
                        px-4 py-3 rounded-lg text-left text-sm font-medium transition-all
                        ${activeSection === link.href.slice(1)
                          ? 'text-primary bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }
                      `}
                    >
                      {link.label}
                    </button>
                  ))}
                  <div className="border-t border-border my-4" />
                  <a 
                    href="https://neuro-mesh.io" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground flex items-center gap-2"
                  >
                    Main Site
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
